/**
 * Image Asset Center — load-failure classification, silent read retry and
 * toast de-duplication (VistoeraTASK-00522 Phase A).
 *
 * Framework-agnostic on purpose (no React / antd import) so the exact
 * decisions the page makes are unit-testable head-less.
 *
 * Why this exists: an axios request that never receives an HTTP response
 * (Render redeploy window -> edge 502/503 without CORS headers, dropped
 * connection, browser abort) surfaces as the opaque message "Network Error"
 * with `status === undefined`. The page used to toast that raw message once
 * per failing request (library list + collections list + every upload
 * completion refresh), which looked like the whole site was offline even
 * though every other API was fine a moment later. This module turns each
 * failure into an accurate, single, recoverable state.
 */

export type LoadErrorKind =
  | 'cancelled'
  | 'offline'
  | 'timeout'
  | 'network'
  | 'unavailable'
  | 'rateLimited'
  | 'server'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'other'

export interface ApiErrorLike {
  status?: number
  code?: string
  canceled?: boolean
  message?: string
  name?: string
}

export interface ClassifiedLoadError {
  kind: LoadErrorKind
  status?: number
  /** Server-provided detail for a plain 4xx (never the opaque axios text). */
  serverMessage?: string
}

const OPAQUE_MESSAGES = new Set(['network error', 'failed to fetch', 'load failed', 'networkerror when attempting to fetch resource.'])

export function isCancelledError(err: unknown): boolean {
  const e = err as ApiErrorLike | null | undefined
  if (!e) return false
  if (e.canceled === true) return true
  if (e.code === 'ERR_CANCELED') return true
  if (e.name === 'AbortError' || e.name === 'CanceledError') return true
  return false
}

export function classifyLoadError(err: unknown, opts?: { offline?: boolean }): ClassifiedLoadError {
  const e = (err ?? {}) as ApiErrorLike
  if (isCancelledError(err)) return { kind: 'cancelled' }
  const status = typeof e.status === 'number' ? e.status : undefined
  if (status === undefined) {
    if (opts?.offline) return { kind: 'offline' }
    if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return { kind: 'timeout' }
    return { kind: 'network' }
  }
  if (status === 401) return { kind: 'auth', status }
  if (status === 403) return { kind: 'forbidden', status }
  if (status === 404) return { kind: 'notFound', status }
  if (status === 429) return { kind: 'rateLimited', status }
  if (status === 502 || status === 503 || status === 504) return { kind: 'unavailable', status }
  if (status >= 500) return { kind: 'server', status }
  const msg = (e.message || '').trim()
  return {
    kind: 'other',
    status,
    serverMessage: msg && !OPAQUE_MESSAGES.has(msg.toLowerCase()) ? msg : undefined,
  }
}

/** Failures worth a silent automatic retry (the server/edge dropped this one
 * request); never auth / permission / not-found / validation. */
export function isTransientLoadKind(kind: LoadErrorKind): boolean {
  return kind === 'network' || kind === 'timeout' || kind === 'unavailable' || kind === 'rateLimited'
}

export interface ReadRetryOptions {
  /** Total tries including the first (default 3). */
  attempts?: number
  baseDelayMs?: number
  maxDelayMs?: number
  signal?: AbortSignal
  offline?: () => boolean
  onRetry?: (info: { attempt: number; error: ClassifiedLoadError }) => void
  /** Injectable for tests. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
}

export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(Object.assign(new Error('canceled'), { canceled: true }))
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(Object.assign(new Error('canceled'), { canceled: true }))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Runs an idempotent READ with a bounded, silent retry on transient failures
 * (default 3 tries, 0.8s / 1.6s backoff). No toast per attempt - the caller
 * only learns about the FINAL outcome. A cancelled request rethrows
 * immediately and is never retried.
 */
export async function withReadRetry<T>(
  fn: (signal?: AbortSignal) => Promise<T>,
  opts: ReadRetryOptions = {},
): Promise<T> {
  const attempts = Math.max(1, opts.attempts ?? 3)
  const base = opts.baseDelayMs ?? 800
  const max = opts.maxDelayMs ?? 8000
  const sleep = opts.sleep ?? abortableSleep
  let lastErr: unknown
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fn(opts.signal)
    } catch (err) {
      lastErr = err
      if (isCancelledError(err) || opts.signal?.aborted) throw err
      const classified = classifyLoadError(err, { offline: opts.offline?.() })
      // Offline will not fix itself in 2 seconds - surface it immediately.
      if (attempt === attempts || !isTransientLoadKind(classified.kind)) throw err
      opts.onRetry?.({ attempt, error: classified })
      await sleep(Math.min(base * 2 ** (attempt - 1), max), opts.signal)
    }
  }
  throw lastErr
}

/**
 * Same failure shown again within `windowMs` is suppressed (it is already on
 * screen). Keyed by `kind|scope` so a DIFFERENT failure still gets through.
 */
export function createNotifyDeduper(windowMs = 8000, now: () => number = () => Date.now()) {
  const last = new Map<string, number>()
  return {
    shouldNotify(key: string): boolean {
      const t = now()
      const prev = last.get(key)
      if (prev !== undefined && t - prev < windowMs) return false
      last.set(key, t)
      return true
    },
    reset(key?: string) {
      if (key) last.delete(key)
      else last.clear()
    },
  }
}

export interface UrlPathParts {
  path: string
  method: string
}

/** Structured, secret-free diagnostic line for a failed request. */
export function describeFailure(parts: {
  path: string
  method?: string
  error: ClassifiedLoadError
  durationMs: number
  retryCount: number
  stage: string
}) {
  return {
    request_url_path: parts.path,
    http_method: (parts.method || 'GET').toUpperCase(),
    http_status: parts.error.status ?? null,
    error_kind: parts.error.kind,
    request_duration_ms: Math.round(parts.durationMs),
    retry_count: parts.retryCount,
    stage: parts.stage,
  }
}
